/**
 * Component for our main Navigation sidebar
 * in the dashboard.
 *
 * @component
 */

import { Menu } from 'antd';
import { useContext, useEffect, useState } from 'react';
import { Link, withRouter } from 'react-router-dom';
import { Progress } from 'antd';
import {
  SolidIconBarChart,
  SolidIconBoxPackage,
  SolidIconBoxSetting,
  SolidIconBriefcase,
  SolidIconEarthSetting,
  SolidIconEmailSetting,
  SolidIconHome,
  SolidIconPackage,
} from '../../../components/CustomReturnsIcons';
import { SubscriptionContext } from '../../../contexts/SubscriptionContext';

const { SubMenu } = Menu;
const Navigation = withRouter((props) => {
  const context = useContext(SubscriptionContext);
  const { location } = props;
  const [selectedKeys, setSelectedKeys] = useState([location.pathname]);
  const [openKeys, setOpenKeys] = useState([]);

  useEffect(() => {
    const pathname = location.pathname;
    setSelectedKeys([pathname]);

    const newOpenKeys = [];
    if (pathname.includes('/dashboard/analytics')) {
      newOpenKeys.push('analytics-settings');
    }
    if (pathname.includes('/dashboard/settings')) {
      newOpenKeys.push('return-settings');
    }
    if (pathname.includes('/dashboard/brand')) {
      newOpenKeys.push('brand-settings');
    }
    if (pathname.includes('/dashboard/email')) {
      newOpenKeys.push('email-settings');
    }
    if (pathname.includes('/dashboard/connect')
      && (!pathname.includes('shopifyappstore') && !pathname.includes('shopifybilling'))) {
      newOpenKeys.push('integration-settings');
    }
    if (pathname.includes('/dashboard/account')) {
      newOpenKeys.push('account-settings');
    }
    if (pathname.includes('/dashboard/returns')) {
      newOpenKeys.push('dashboard-return');
    }
    setOpenKeys(newOpenKeys);
  }, [location.pathname]);

  const handleMenuClick = (e) => {
    setSelectedKeys([e.key]);
  };

  if (location.pathname.includes('/dashboard/settings/exchanges')) {
    selectedKeys.push('/dashboard/settings/exchanges');
  }

  if (location.pathname.includes('/dashboard/analytics/trends')) {
    selectedKeys.push('/dashboard/analytics/trends');
  }
  
  const contextIntervalTime = context?.intervalTime || '';
  const contextUsageQuota =
    contextIntervalTime === 'EVERY_30_DAYS'
      ? context?.monthlyQuota || 0
      : (context?.monthlyQuota || 0) * 12;

  let percentOfQuota = 0;
  if (contextUsageQuota === 0) {
    percentOfQuota = 100;
  } else if (!context || context.usageChargeCount === 0) {
    percentOfQuota = 0;
  } else if ((context.usageChargeCount || 0) > contextUsageQuota) {
    percentOfQuota = 100;
  } else {
    percentOfQuota = (
            ((context.usageChargeCount || 0) / contextUsageQuota) *
            100
          ).toFixed(0);
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        height: '100%',
        rowGap: '24px',
      }}
    >
      <div>
        <Menu
          mode="inline"
          selectedKeys={selectedKeys}
          onClick={handleMenuClick}
          openKeys={openKeys}
          onOpenChange={setOpenKeys}
        >
          <Menu.Item key="/dashboard">
            <Link to="/dashboard">
              <span className=" flex-row flex-row--noMargin flex-middle-xxs u-flexWrap--nowrap u-columnGap--xs">
                <SolidIconHome size="sm" />
                <span
                  className="TextHeading TextHeading--xxxs u-marginBottom--none"
                  data-intercom-target="home-navigation"
                >
                  Home
                </span>
              </span>
            </Link>
          </Menu.Item>
          <SubMenu
            key="dashboard-return"
            title={
              <span className=" flex-row flex-row--noMargin flex-middle-xxs u-flexWrap--nowrap u-columnGap--xs">
                <SolidIconBoxPackage size="sm" />
                <span
                  className="TextHeading TextHeading--xxxs u-marginBottom--none"
                  data-intercom-target="returns-navigation"
                >
                  Returns
                </span>
              </span>
            }
          >
            <Menu.Item key="/dashboard/returns">
              <Link to="/dashboard/returns">
                <span className=" flex-row flex-row--noMargin flex-middle-xxs u-flexWrap--nowrap u-columnGap--xs">
                  <span
                    className="TextBody TextBody--xxs u-marginBottom--none"
                    data-intercom-target="by-return-dashboard"
                  >
                    By Returns
                  </span>
                </span>
              </Link>
            </Menu.Item>
            <Menu.Item key="/dashboard/returns/orders">
              <Link to="/dashboard/returns/orders">
                <span className=" flex-row flex-row--noMargin flex-middle-xxs u-flexWrap--nowrap u-columnGap--xs">
                  <span
                    className="TextBody TextBody--xxs u-marginBottom--none"
                    data-intercom-target="by-order-dashboard"
                  >
                    By Orders
                  </span>
                </span>
              </Link>
            </Menu.Item>
          </SubMenu>
          <SubMenu
            key="analytics-settings"
            title={
              <span className=" flex-row flex-row--noMargin flex-middle-xxs u-flexWrap--nowrap u-columnGap--xs">
                <SolidIconBarChart size="sm" />
                <span
                  className="TextHeading TextHeading--xxxs u-marginBottom--none"
                  data-intercom-target="analytics-navigation"
                >
                  Analytics
                </span>
              </span>
            }
          >
            <Menu.Item key="/dashboard/analytics/dashboard">
              <Link to="/dashboard/analytics/dashboard">
                <span
                  className="TextBody TextBody--xxs u-marginBottom--none"
                  data-intercom-target="overview-analytics-navigation"
                >
                  Overview
                </span>
              </Link>
            </Menu.Item>
            <Menu.Item key="/dashboard/analytics/reports/reasons/by-product">
              <Link to="/dashboard/analytics/reports/reasons/by-product">
                <span
                  className="TextBody TextBody--xxs u-marginBottom--none"
                  data-intercom-target="reports-analytics-navigation"
                >
                  Reports
                </span>
              </Link>
            </Menu.Item>
            <Menu.Item key="/dashboard/analytics/trends">
              <Link to="/dashboard/analytics/trends">
                <span
                  className="TextBody TextBody--xxs u-marginBottom--none"
                  data-intercom-target="trends-analytics-navigation"
                >
                  Trends
                </span>
              </Link>
            </Menu.Item>
            <Menu.Item key="/dashboard/analytics/customer">
              <Link to="/dashboard/analytics/customer">
                <div className=" flex-row flex-row--noMargin flex-middle-xxs u-flexWrap--nowrap u-columnGap--xs">
                  <div
                    className="TextBody TextBody--xxs u-marginBottom--none u-marginRight--xxs"
                    data-intercom-target="customers-analytics-navigation"
                  >
                    Customers
                  </div>
                </div>
              </Link>
            </Menu.Item>
            <Menu.Item key="/dashboard/analytics/export">
              <Link to="/dashboard/analytics/export">
                <span
                  className="TextBody TextBody--xxs u-marginBottom--none"
                  data-intercom-target="data-export-analytics-navigation"
                >
                  Data export
                </span>
              </Link>
            </Menu.Item>
          </SubMenu>
          <SubMenu
            key="return-settings"
            title={
              <span className=" flex-row flex-row--noMargin flex-middle-xxs u-flexWrap--nowrap u-columnGap--xs">
                <SolidIconBoxSetting size="sm" />
                <span
                  className="TextHeading TextHeading--xxxs u-marginBottom--none"
                  data-intercom-target="configuration-navigation"
                >
                  Configuration
                </span>
              </span>
            }
          >
            <Menu.Item key="/dashboard/settings">
              <Link to="/dashboard/settings">
                <span
                  className="TextBody TextBody--xxs u-marginBottom--none"
                  data-intercom-target="returns-configuration-navigation"
                >
                  Returns
                </span>
              </Link>
            </Menu.Item>
            <Menu.Item key="/dashboard/settings/exchanges">
              <Link to="/dashboard/settings/exchanges">
                <span
                  className="TextBody TextBody--xxs u-marginBottom--none"
                  data-intercom-target="exchanges-configuration-navigation"
                >
                  Exchanges
                </span>
              </Link>
            </Menu.Item>
            {context && context.isWarrantyAvailable && (
              <Menu.Item key="/dashboard/settings/warranties">
                <Link to="/dashboard/settings/warranties">
                  <span className="TextBody TextBody--xxs u-marginBottom--none">
                    Warranties
                  </span>
                </Link>
              </Menu.Item>
            )}
            <Menu.Item key="/dashboard/settings/refunds">
              <Link to="/dashboard/settings/refunds">
                <span
                  className="TextBody TextBody--xxs u-marginBottom--none"
                  data-intercom-target="refunds-configuration-navigation"
                >
                  Refunds
                </span>
              </Link>
            </Menu.Item>
            <Menu.Item key="/dashboard/settings/incentives">
              <Link to="/dashboard/settings/incentives">
                <span
                  className="TextBody TextBody--xxs u-marginBottom--none"
                  data-intercom-target="incentives-configuration-navigation"
                >
                  Incentives
                </span>
              </Link>
            </Menu.Item>
            <Menu.Item key="/dashboard/settings/carriers">
              <Link to="/dashboard/settings/carriers">
                <span
                  className="TextBody TextBody--xxs u-marginBottom--none"
                  data-intercom-target="prepaid-labels-configuration-navigation"
                >
                  Prepaid Labels
                </span>
              </Link>
            </Menu.Item>
            <Menu.Item key="/dashboard/settings/inventory">
              <Link to="/dashboard/settings/inventory">
                <span
                  className="TextBody TextBody--xxs u-marginBottom--none"
                  data-intercom-target="inventory-management-configuration-navigation"
                >
                  Inventory Management
                </span>
              </Link>
            </Menu.Item>
            <Menu.Item key="/dashboard/settings/rules">
              <Link to="/dashboard/settings/rules">
                <span
                  className="TextBody TextBody--xxs u-marginBottom--none"
                  data-intercom-target="advanced-rules-configuration-navigation"
                >
                  Advanced Rules
                </span>
              </Link>
            </Menu.Item>

            <Menu.Item key="/dashboard/settings/sync">
              <Link to="/dashboard/settings/sync">
                <span
                  className="TextBody TextBody--xxs u-marginBottom--none"
                  data-intercom-target="shopify-syncing-configuration-navigation"
                >
                  Shopify Syncing
                </span>
              </Link>
            </Menu.Item>
            <Menu.Item key="/dashboard/settings/payment">
              <Link to="/dashboard/settings/payment">
                <span
                  className="TextBody TextBody--xxs u-marginBottom--none"
                  data-intercom-target="payments-configuration-navigation"
                >
                  Payments
                </span>
              </Link>
            </Menu.Item>
          </SubMenu>
          <SubMenu
            key="brand-settings"
            title={
              <span className=" flex-row flex-row--noMargin flex-middle-xxs u-flexWrap--nowrap u-columnGap--xs">
                <SolidIconEarthSetting size="sm" />
                <span
                  className="TextHeading TextHeading--xxxs u-marginBottom--none u-marginRight--xxs"
                  data-intercom-target="returns-portal-navigation"
                >
                  Returns portal
                </span>
              </span>
            }
          >
            <Menu.Item key="/dashboard/brand">
              <Link to="/dashboard/brand">
                <span
                  className="TextBody TextBody--xxs u-marginBottom--none"
                  data-intercom-target="customizations-returns-portal-navigation"
                >
                  Customizations
                </span>
              </Link>
            </Menu.Item>
            <Menu.Item key="/dashboard/brand/custom-domain">
              <Link to="/dashboard/brand/custom-domain">
                <span
                  className="TextBody TextBody--xxs u-marginBottom--none"
                  data-intercom-target="own-domain-returns-portal-navigation"
                >
                  Own domain
                </span>
              </Link>
            </Menu.Item>
            <Menu.Item key="/dashboard/brand/translations">
              <Link to="/dashboard/brand/translations">
                <span className="TextBody TextBody--xxs u-marginBottom--none">
                  Translations
                </span>
              </Link>
            </Menu.Item>
          </SubMenu>
          <SubMenu
            key="email-settings"
            title={
              <span className=" flex-row flex-row--noMargin flex-middle-xxs u-flexWrap--nowrap u-columnGap--xs">
                <SolidIconEmailSetting size="sm" />
                <span
                  className="TextHeading TextHeading--xxxs u-marginBottom--none"
                  data-intercom-target="notifications-navigation"
                >
                  Notifications
                </span>
              </span>
            }
          >
            <Menu.Item key="/dashboard/email/admin">
              <Link to="/dashboard/email/admin">
                <span
                  className="TextBody TextBody--xxs u-marginBottom--none"
                  data-intercom-target="staff-emails-notifications-navigation"
                >
                  Staff emails
                </span>
              </Link>
            </Menu.Item>
            <Menu.Item key="/dashboard/email">
              <Link to="/dashboard/email">
                <span
                  className="TextBody TextBody--xxs u-marginBottom--none"
                  data-intercom-target="customer-emails-notifications-navigation"
                >
                  Customer emails
                </span>
              </Link>
            </Menu.Item>
          </SubMenu>
          <SubMenu
            key="integration-settings"
            title={
              <span className=" flex-row flex-row--noMargin flex-middle-xxs u-flexWrap--nowrap u-columnGap--xs">
                <SolidIconPackage size="sm" />
                <span
                  className="TextHeading TextHeading--xxxs u-marginBottom--none"
                  data-intercom-target="integrations-navigation"
                >
                  Integrations
                </span>
              </span>
            }
          >
            <Menu.Item key="/dashboard/connect/platform">
              <Link to="/dashboard/connect/platform">
                <span
                  className="TextBody TextBody--xxs u-marginBottom--none"
                  data-intercom-target="platforms-integrations-navigation"
                >
                  Platforms
                </span>
              </Link>
            </Menu.Item>
            <Menu.Item key="/dashboard/connect/courier">
              <Link to="/dashboard/connect/courier">
                <span
                  className="TextBody TextBody--xxs u-marginBottom--none"
                  data-intercom-target="carriers-integrations-navigation"
                >
                  Carriers
                </span>
              </Link>
            </Menu.Item>
          </SubMenu>
        </Menu>
      </div>

      <div>
        <Menu
          mode="inline"
          selectedKeys={selectedKeys}
          onClick={handleMenuClick}
          openKeys={openKeys}
          onOpenChange={setOpenKeys}
        >
          <SubMenu
            key="account-settings"
            title={
              <span className=" flex-row flex-row--noMargin flex-middle-xxs u-flexWrap--nowrap u-columnGap--xs">
                <SolidIconBriefcase size="sm" />
                <span
                  className="TextHeading TextHeading--xxxs u-marginBottom--none"
                  data-intercom-target="account-navigation"
                >
                  Account
                </span>
              </span>
            }
          >
            {context && context.directBilling ? (
              <Menu.Item key="/dashboard/account/plan" className="navAccount">
                <Link to="/dashboard/account/plan">
                  <span
                    className="TextBody TextBody--xxs u-marginBottom--none"
                    data-intercom-target="your-plan-account-navigation"
                  >
                    Your plan
                  </span>
                </Link>
              </Menu.Item>
            ) : (
              <Menu.Item
                key="/dashboard/account/profile"
                className="navAccount"
              >
                <Link to="/dashboard/account/profile">
                  <span
                    className="TextBody TextBody--xxs u-marginBottom--none"
                    data-intercom-target="your-plan-account-navigation"
                  >
                    Your plan
                  </span>
                </Link>
              </Menu.Item>
            )}
            <Menu.Item key="/dashboard/account/api">
              <Link to="/dashboard/account/api">
                <span
                  className="TextBody TextBody--xxs u-marginBottom--none"
                  data-intercom-target="developers-account-navigation"
                >
                  Developers
                </span>
              </Link>
            </Menu.Item>
            <Menu.Item key="/dashboard/account/audit-log">
              <Link to="/dashboard/account/audit-log">
                <span
                  className="TextBody TextBody--xxs u-marginBottom--none"
                  data-intercom-target="audit-log-account-navigation"
                >
                  Audit log
                </span>
              </Link>
            </Menu.Item>
            <Menu.Item key="/dashboard/tutorials">
              <a
                href="https://support.richreturns.io/en?utm_source=merchant_panel"
                target="_blank"
                rel="noopener noreferrer"
              >
                <span
                  className="TextBody TextBody--xxs u-marginBottom--none"
                  data-intercom-target="tutorials-faq-account-navigation"
                >
                  Tutorials & FAQ
                </span>
              </a>
            </Menu.Item>
            <Menu.Item key="/dashboard/logout">
              <Link to="/dashboard/logout">
                <span
                  className="TextBody TextBody--xxs u-marginBottom--none"
                  data-intercom-target="log-out-account-navigation"
                >
                  Log out
                </span>
              </Link>
            </Menu.Item>
          </SubMenu>
        </Menu>
        {/* Monthly Return Quota Display */}
        {contextUsageQuota > 0 && (
          <div
            style={{
              backgroundColor:
                percentOfQuota >= 100
                  ? '#f9e2e2'
                  : percentOfQuota >= 81
                  ? '#fff3cd'
                  : '#e3f1f4',
              padding: '15px',
              borderRadius: '4px',
              border:
                '1px, solid ' +
                (percentOfQuota >= 100
                  ? '#f5c6cb'
                  : percentOfQuota >= 81
                  ? '#ffeeba'
                  : '#bee5eb'),
              margin: '15px 0px 15px 10px',
            }}
          >
            <div
              style={{
                fontSize: '12px',
                fontWeight: 'bold',
                marginBottom: '6px',
                color: '#333',
              }}
            >
              Monthly Return Quota
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Progress
                percent={parseFloat(percentOfQuota)}
                strokeColor={
                  percentOfQuota >= 100
                    ? '#c26767'
                    : '#67c2c2'
                }
                trailColor="#f0f0f0"
                size="small"
                showInfo={false}
                style={{ flex: 1, marginBottom: 0 }}
              />
              <span style={{ fontSize: 12, color: '#595959' }}>
                {percentOfQuota}%
              </span>
            </div>

            {percentOfQuota >= 100 && (
              <div
                style={{
                  fontSize: '10px',
                  marginTop: '4px',
                  marginBottom: '4px',
                  color: '#333',
                }}
              >
                You've reached your plan's quota for this billing period. Extra
                returns will incur additional charges.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
});

export default Navigation;
